// Config plugin.
import * as actions from './actions'
import { getConfiguration } from './selectors'

// Other plugins.
import { logManager, API_LOG_TAG } from '../../logs'
import parseCallConfigs from '../../call/configs'

import { cloneDeep } from 'lodash/fp'

const log = logManager.getLogger('CONFIG')

export default function api (context) {
  const configApi = {
    /**
     * Gets the current configuration Object. This is the object that is initially set as part of SDK creation using 'create' function.
     *
     * @public
     * @static
     * @memberof api
     * @requires config
     * @method getConfig
     * @returns {Object} A configuration Object.
     */
    getConfig: function () {
      log.debug(API_LOG_TAG + 'getConfig')
      return getConfiguration(context.getState())
    },

    /**
     * Update the configuration values for the SDK to use.
     *
     * This API will only modify the configurations provided, leaving other configurations
     *   as they were originally set, by performing a merge of the new values into the
     *   previous values.
     *
     * Please note that updating the {@link config#config.call call.removeH264Codecs} configuration will not immediately change the SDP handlers used for a call. If you want to add or remove the
     *   h264 codec remover sdp handler you should follow this procedure:
     *   1. Update the config for removeH264Codecs using the {@link api.updateConfig updateConfig} API.
     *   2. Update the sdp handler list using the {@link call.setSdpHandlers setSdpHandlers} API and provide any client defined SDP handler functions.
     *
     *      NOTE: You can get the currently defined SDP handler functions with the {@link api.getConfig getConfig} API.
     *
     * Please note that the object provided to the `updateConfig` API may be different
     *   than the object retrieved from the {@link api.getConfig getConfig} API. This may happen when a format
     *   change has happened and the SDK modifies the provided format to alleviate
     *   backwards-compatibility issues. We recommend ensuring the configurations you
     *   provide are as described by the {@link config} section.
     *
     * @public
     * @static
     * @memberof api
     * @requires config
     * @method updateConfig
     * @param {Object} newConfigValues Key-value pairs that will be placed into the store. See {@link config} for details on what key-value pairs are available for use.
     * @example
     * // Instantiate the SDK with certain configs.
     * const client = create({
     *   authentication: { ... },
     *   logs: { ... },
     *   ...
     * })
     *
     * // Modify a subsection of the configs at a later time.
     * // This will only update the specified configurations.
     * client.updateConfig({
     *     logs: {
     *       loglevel: 'DEBUG'
     *     }
     * })
     */
    updateConfig: function (newConfigValues) {
      log.debug(API_LOG_TAG + 'updateConfig: ', newConfigValues)

      // Clone the configs so we don't mutate the application's object.
      const configs = cloneDeep(newConfigValues)

      // Validate the new config provided by the application.
      if (configs.call) {
        // TODO: Should add validation for all configs, not just the call-related ones
        //      (by parsing using the specific 'v8nValidation' constant,
        //       available in each plugin).
        configs.call = parseCallConfigs(configs.call)
      }

      context.dispatch(actions.update(configs))
    }
  }

  return configApi
}
